import styled from "@emotion/styled";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  Box,
  Column,
  Row,
  Space,
  type TextProps,
  Txt,
} from "@stenajs-webui/core";
import {
  Banner,
  Cardy,
  CardyBody,
  Icon,
  stenaCalendar,
  stenaStatusNoShow,
  ValueTable,
} from "@stenajs-webui/elements";
import { cssColor } from "@stenajs-webui/theme";
import { parseISO } from "date-fns";
import { type FC, type ReactNode, useMemo } from "react";

import {
  type InformationMessage,
  resolveInformationMessageBannerVariant,
} from "../../../../../common-ui/information-message/informationMessageUtils";
import { FlatButtonLink } from "../../../../../common-ui/Link";
import { PortDetailsForSailing } from "../../../../../common-ui/PortDetailsForSailing";
import { getShortArrivalInformation } from "../../../../../common-ui/sailing-info/utils/getExtendedArrivalInformation";
import { ShadowlessCard } from "../../../../../common-ui/ShadowlessCard";
import { TripDetails } from "../../../../../common-ui/TripDetails";
import type { DetailedBooking } from "../../../../../common/graphql/fragments/gql/DetailedBooking";
import type { DetailedBookingLateHandlingStatus } from "../../../../../common/graphql/fragments/gql/DetailedBooking";
import type { Sailing } from "../../../../../common/graphql/fragments/gql/Sailing";
import { useLocalDateFormat } from "../../../../../common/hooks/useLocalDateFormat";
import { modalRouteCreators } from "../../../../../common/routes/AppRoutes";
import { useLinkModalProps } from "../../../../../common/routes/useLinkModalProps";
import { Information } from "../../../../../common/texts";
import { useUpdateFormState } from "../../../../../common/update-information/hooks/useUpdateFormState";
import { formatDateTime } from "../../../../../common/utils/dates/dateUtils";
import { INTERMODAL_ICON } from "../../../grid/utils/bookingSpecialProperties";
import { shouldShowShareBooking } from "../../../share/util/shareUtil";
import { bookingDetailsRows, type RowType } from "../../utils/detailsRows";
import { BookingDetailsViewBannersContainer } from "../BookingDetailsViewBannersContainer";
import { BookingUpdateStatusMessage } from "../BookingUpdateStatusMessage";
import { ShareBookingCard } from "../ShareBookingCard";
import { DetailsRow } from "./DetailsRow";

export interface BookingDetailsViewContentProps {
  booking: DetailedBooking;
  cancelStatusMessages: InformationMessage[];
}

const ValueTableWrapper = styled(Cardy)`
  table {
    table-layout: fixed;
  }
`;

const renderRow: (row: RowType) => ReactNode = ({
  className,
  name,
  value,
  suffix,
  collapse = true,
}) => (
  <DetailsRow
    key={name}
    name={name}
    collapse={collapse}
    value={value}
    className={className}
    suffix={suffix}
  />
);

export const BookingDetailsViewContent: FC<BookingDetailsViewContentProps> = ({
  booking,
  cancelStatusMessages,
}) => {
  const { updateSubmitInformation } = useUpdateFormState({
    formId: "Details",
    bookingNo: booking.bookingNo,
  });

  const { lateHandlingStatus } = booking;

  const dateFormat = useLocalDateFormat();

  const intermodalDetailsEditLinkProps = useLinkModalProps(
    modalRouteCreators.intermodalDetailsEdit({
      bookingNo: booking.referenceBookingNo,
    }),
  );

  const rows = useMemo(
    () => bookingDetailsRows(booking, dateFormat),
    [booking, dateFormat],
  );

  const vehicleRegNumber = booking.vehicleRegNo || booking.trailerRegNo;

  const arrivalInformation = booking.sailing
    ? getShortArrivalInformation(
        booking.hasArrived,
        booking.sailing,
        dateFormat,
      )
    : null;

  return (
    <Box className={"t_booking_details_view"}>
      <Box spacing={2} maxWidth={750}>
        <PortDetailsForSailing
          sailing={booking.sailing}
          bookingStatus={booking.bookingStatus.status}
        />
      </Box>

      <Box maxWidth={980} spacing gap>
        {booking.referenceBookingNo > 0 && (
          <Banner
            icon={INTERMODAL_ICON}
            variant={"info"}
            text={`${Information.PartOfIntermodalBookingPrefix} ${booking.referenceBookingNo}.`}
            contentRight={
              <FlatButtonLink
                {...intermodalDetailsEditLinkProps}
                className={"t_intermodal_open_booking_link"}
                label={"Open intermodal booking"}
              />
            }
          />
        )}

        <BookingUpdateStatusMessage
          updateSubmitInformation={updateSubmitInformation}
          submitting={false}
        />

        {cancelStatusMessages.map((cancelStatusMessage, index) => (
          <Banner
            key={index}
            variant={resolveInformationMessageBannerVariant(
              cancelStatusMessage.type,
            )}
            headerText={cancelStatusMessage.message}
          />
        ))}
        <BookingDetailsViewBannersContainer
          booking={booking}
          updateSubmitInformation={updateSubmitInformation}
        />
      </Box>

      <Box flexDirection={["column", null, "row"]} gap={2}>
        <ValueTableWrapper maxWidth={[null, null, 600]} flex={2}>
          <CardyBody>
            <ValueTable disableBorder variant={"compact"}>
              {rows.map((row) => renderRow(row))}
            </ValueTable>
          </CardyBody>
        </ValueTableWrapper>

        <Column justifyContent={"space-between"} gap={2}>
          <SailingDetails
            sailing={booking.sailing}
            releasedAt={booking.releasedAt}
            arrivalInformation={arrivalInformation}
            dateFormat={dateFormat}
            lateHandlingStatus={lateHandlingStatus}
            dateModified={booking.dateModified}
          />
          <Column width={[null, null, "min-content"]}>
            {shouldShowShareBooking(
              vehicleRegNumber,
              booking.bookingStatus.status,
              booking.bookingQuayStatus?.status,
            ) && (
              <ShareBookingCard
                bookingNo={booking.bookingNo}
                vehicleRegNo={booking.vehicleRegNo}
              />
            )}
          </Column>
        </Column>
      </Box>
      <Space num={2} />
    </Box>
  );
};

interface SailingDetailsProps {
  arrivalInformation: string | null;
  dateFormat: string;
  dateModified: string;
  lateHandlingStatus: DetailedBookingLateHandlingStatus | null;
  releasedAt: string | null;
  sailing: Sailing;
}

const SailingDetails = ({
  sailing,
  dateFormat,
  dateModified,
  releasedAt,
  lateHandlingStatus,
  arrivalInformation,
}: SailingDetailsProps) => (
  <Box gap={2}>
    <ShadowlessCard indent={2} spacing={3} gap={1}>
      <TripDetails
        shipName={sailing.shipName}
        releasedAt={releasedAt}
        sailingType={sailing.sailingType}
        arrivalInformation={arrivalInformation}
        dateFormat={dateFormat}
      />
    </ShadowlessCard>
    <Box gap={1} indent={2}>
      {lateHandlingStatus && (
        <BookingValueRow
          icon={stenaStatusNoShow}
          label={lateHandlingStatus.label}
          value={formatDateTime(
            parseISO(lateHandlingStatus.statusDate),
            dateFormat,
          )}
          color={cssColor("--lhds-color-red-500")}
        />
      )}
      <BookingValueRow
        icon={stenaCalendar}
        label={"Last updated"}
        value={formatDateTime(parseISO(dateModified), dateFormat)}
      />
    </Box>
  </Box>
);

interface BookingLabelWithDateProps extends Pick<TextProps, "color"> {
  icon: IconDefinition;
  label: string;
  value: string;
}

const BookingValueRow: FC<BookingLabelWithDateProps> = ({
  value,
  label,
  color,
  icon,
}) => (
  <Row alignItems={"center"} gap={2}>
    <Icon color={color} icon={icon} size={20} />
    <Txt>
      <Txt color={color}>{label}</Txt>
      <Txt> – </Txt>
      <Txt>{value}</Txt>
    </Txt>
  </Row>
);
